import { useState, useEffect, useRef, useCallback } from 'react'

// Global state for auto-refresh (simple approach)
let globalAutoRefresh = true
const refreshCallbacks = new Set()

const useReportRefresh = (
  refreshFunction,
  dependencies = [],
  intervalMs = 60000
) => {
  const [autoRefresh, setAutoRefresh] = useState(globalAutoRefresh)
  const intervalRef = useRef(null)
  const refreshFunctionRef = useRef(refreshFunction)

  // Keep refreshFunction ref up to date
  useEffect(() => {
    refreshFunctionRef.current = refreshFunction
  }, [refreshFunction])

  const handleToggleAutoRefresh = useCallback(() => {
    globalAutoRefresh = !globalAutoRefresh
    // Update all components
    refreshCallbacks.forEach((callback) => callback(globalAutoRefresh))
  }, [])

  // Register this component for global updates
  useEffect(() => {
    const updateCallback = (newState) => setAutoRefresh(newState)
    refreshCallbacks.add(updateCallback)
    return () => refreshCallbacks.delete(updateCallback)
  }, [])

  const handleManualRefresh = useCallback(() => {
    if (refreshFunctionRef.current) {
      refreshFunctionRef.current()
    }
  }, [])

  // Auto-refresh functionality
  useEffect(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (autoRefresh && refreshFunctionRef.current) {
      intervalRef.current = setInterval(() => {
        if (refreshFunctionRef.current) {
          refreshFunctionRef.current()
        }
      }, intervalMs)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [autoRefresh, intervalMs, ...dependencies])

  return {
    autoRefresh,
    handleToggleAutoRefresh,
    handleManualRefresh
  }
}

export default useReportRefresh
